import styled from '@emotion/styled'
import {
  ApiClient,
  ApiModelPortfolioUsage,
  ApiPortfolioStatus,
  ApiSavedPortfolio,
  ApiStrategyTemplate
} from 'api/rtk-api'
import ToastNotification from 'components/core-v2/atoms/toast-notification/toastNotification'
import { useEffect, useMemo, useState } from 'react'

import {
  CheckboxWithLabel,
  GenericValidationMessages,
  Icon,
  IconButton,
  PortfolioRestrictions,
  Space,
  Tabs,
  Typography,
  ValidationMessage
} from '@/components/core-v2'
import { PORTFOLIO_TYPE } from '@/constants'
import { AssetSummaryItem } from '@/modules/tradebuilder-v2/modules/03-portfolio/types'
import { Errors } from '@/modules/tradebuilder-v2/util'
import { TradingStatusClosedAlert } from '@/modules/tradebuilder-v2/util/Alerts'
import { useUser } from '@/utilities/hooks/useUser'

import { PortfolioDetailsTab } from '../../detailed-tabs/details'
import { usePortfolioPermissions } from '../../hooks'
import { EditPortfoliosActions } from '../../state/edit-portfolio-slice'
import { useAppSelector } from '../../store/hooks'
import { getContentHeight } from '../../utils'
import { PortfolioDetailsHeader } from './portfolio-details-header'
import { PortfolioDetailsType } from './portfolio-details-type'

const StyledPortfolioTabs = styled.div`
  margin-left: -8px;
`
interface StyledPortfolioContentsProps {
  approvalStatus: ApiPortfolioStatus
  isNew: boolean
}

const StyledPortfolioContents = styled('div')<StyledPortfolioContentsProps>(
  ({ approvalStatus, isNew }) => ({
    height: `calc(100vh - ${getContentHeight(approvalStatus, isNew)}px)`,
    overflowY: 'auto'
  })
)

const StyledCheckboxWithLabelWrapper = styled.div`
  .checkmark {
    margin-top: 3px;
  }
`

export type PortfolioDetailsPanelProps = {
  actions: EditPortfoliosActions
  allClients: ApiClient[]
  approvalByPM: boolean
  approvalSuccess: boolean
  areDisallowedStrategiesOverridden: boolean
  assetPerformances: AssetSummaryItem[]
  availableCptyIds: string[]
  availableStrategies: ApiStrategyTemplate[]
  canProceed: boolean
  canProceedApproval: boolean
  canUserSave: boolean
  errors: Errors
  isNew: boolean
  onApprovePortfolio: () => void
  onChangeApprovalStatus: (status: 'Approved' | 'Rejected' | '') => void
  onChangeApprovalUserName: (userName: string) => void
  portfolioUsage: ApiModelPortfolioUsage
  resetApprovalStatus: () => void
  saveSuccess: boolean
  selectedAssetDuration: string
  selectedPortfolio?: ApiSavedPortfolio
  serverErrors?: string[]
  totalWeight: number
}

export const PortfolioDetailsPanel = ({
  actions,
  allClients,
  approvalByPM,
  approvalSuccess,
  areDisallowedStrategiesOverridden,
  assetPerformances,
  availableCptyIds,
  availableStrategies,
  canProceed,
  canProceedApproval,
  canUserSave,
  errors,
  isNew,
  onApprovePortfolio,
  onChangeApprovalStatus,
  onChangeApprovalUserName,
  portfolioUsage,
  resetApprovalStatus,
  saveSuccess,
  selectedAssetDuration,
  selectedPortfolio,
  serverErrors,
  totalWeight
}: PortfolioDetailsPanelProps) => {
  const { data: user } = useUser()
  const [canProceedWithApproval, setCanProceedWithApproval] = useState<boolean>(false)

  const [selectedTab, setSelectedTab] = useState<string>('details')
  const allowedBuIds = !selectedPortfolio.allowedBusinessUnitIds
    ? []
    : selectedPortfolio.allowedBusinessUnitIds

  const { status: approvalStatus, type } = selectedPortfolio
  const isFundOfFund = type === PORTFOLIO_TYPE.FundOfFunds.key
  const isTypeSet = type === 'None'
  const isAffectedPortfolioManager = useMemo(
    () =>
      user.roles.includes('PortfolioManager') && user.portfolioManagerCategory === 'Cat1' && !isNew,
    [user.roles, user.portfolioManagerCategory, isNew]
  )

  useEffect(() => {
    const canApprove = isAffectedPortfolioManager ? approvalByPM && canProceed : canProceed
    setCanProceedWithApproval(canApprove)
  }, [isAffectedPortfolioManager, canProceed, approvalByPM])

  const state = useAppSelector(state => state.editPortfolios)
  const { isUserPortfolioRestricted } = usePortfolioPermissions(state)

  if (selectedPortfolio) {
    return (
      <>
        {!isNew && (
          <PortfolioDetailsHeader
            approvalStatus={approvalStatus}
            canProceedApproval={canProceedApproval}
            isFundOfFund={isFundOfFund}
            isNew={isNew}
            portfolioId={selectedPortfolio.portfolioId}
            portfolioName={selectedPortfolio.portfolioName}
            resetApprovalStatus={resetApprovalStatus}
            onApprovePortfolio={onApprovePortfolio}
            onChangeApprovalStatus={onChangeApprovalStatus}
            onChangeApprovalUserName={onChangeApprovalUserName}
          />
        )}
        {isNew && (
          <>
            <Typography fontSize="15px" fontWeight="bold" variant="b3">
              Add a new portfolio
            </Typography>
            <PortfolioDetailsType
              portfolioType={selectedPortfolio.type}
              setPortfolioType={actions.setPortfolioType}
            />
          </>
        )}
        {!isTypeSet && (
          <>
            <StyledPortfolioTabs>
              <Tabs.List className="mt-4" spacing="md">
                <Tabs.Tab
                  isSelected={selectedTab === 'details'}
                  value="details"
                  onClick={() => setSelectedTab('details')}
                >
                  Details
                </Tabs.Tab>
                {!isUserPortfolioRestricted && (
                  <Tabs.Tab
                    isSelected={selectedTab === 'restrictions'}
                    value="restrictions"
                    onClick={() => setSelectedTab('restrictions')}
                  >
                    Restrictions and usage
                  </Tabs.Tab>
                )}
              </Tabs.List>
            </StyledPortfolioTabs>
            <StyledPortfolioContents approvalStatus={approvalStatus} isNew={isNew}>
              {selectedTab === 'details' && (
                <Tabs.Panel value="details">
                  <PortfolioDetailsTab
                    actions={actions}
                    areDisallowedStrategiesOverridden={areDisallowedStrategiesOverridden}
                    assetPerformances={assetPerformances}
                    availableCptyIds={availableCptyIds}
                    availableStrategies={availableStrategies}
                    errors={errors}
                    isFundOfFund={isFundOfFund}
                    isNew={isNew}
                    portfolio={selectedPortfolio}
                    readonlyAccess={!canUserSave}
                    selectedAssetDuration={selectedAssetDuration}
                    totalWeight={totalWeight}
                  />
                </Tabs.Panel>
              )}
              {selectedTab === 'restrictions' && (
                <Tabs.Panel value="restrictions">
                  <PortfolioRestrictions
                    allowedBusinessUnitIds={allowedBuIds}
                    clients={allClients}
                    readonlyAccess={!canUserSave}
                    usages={portfolioUsage}
                    onChange={(ids, val) => actions.setBusinessUnitPermissions({ ids, val })}
                  />
                </Tabs.Panel>
              )}
              <GenericValidationMessages errorMessages={serverErrors} />
              <TradingStatusClosedAlert />
              {canUserSave && (
                <>
                  {isAffectedPortfolioManager && (
                    <Space mb={5}>
                      <StyledCheckboxWithLabelWrapper>
                        <CheckboxWithLabel
                          data-testid="cat-1-requirement"
                          onChange={e => actions.setPortfolioManagerChecked(e)}
                        >
                          {`By ticking this box and approving this ${
                            PORTFOLIO_TYPE[selectedPortfolio.type].value
                          } portfolio change, you confirm that you
                  have the necessary authorization( e.g Record(s) of Advice) to enter into the
                  transaction.`}
                        </CheckboxWithLabel>
                      </StyledCheckboxWithLabelWrapper>
                      <ValidationMessage errors={errors} fieldName="portfolio_manager_checked" />
                    </Space>
                  )}
                  <Space isflex mb={6}>
                    <IconButton
                      disabled={!canProceedWithApproval}
                      icon={<Icon name="save" size="default" />}
                      iconPosition="left"
                      operationType="progress"
                      text="Save"
                      onClick={() => actions.savePortfolio()}
                    />
                  </Space>
                </>
              )}
              <ToastNotification
                description={`${selectedPortfolio.portfolioName} saved successfully!`}
                notificationType="success"
                open={saveSuccess}
                title="Portfolio saved"
                onClose={() => actions.setSaveSuccessFalse()}
              />
              <ToastNotification
                description={`${selectedPortfolio.portfolioName} has been set to ${
                  approvalStatus === 'Active' ? 'Approved' : 'Rejected'
                }!`}
                notificationType={approvalStatus === 'Active' ? 'success' : 'warning'}
                open={approvalSuccess}
                title={`Portfolio ${approvalStatus === 'Active' ? 'Approved' : 'Rejected'}`}
                onClose={() => actions.setApprovalSuccess(false)}
              />
            </StyledPortfolioContents>
          </>
        )}
      </>
    )
  }
}
